import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchVehicleInsurances,
  createVehicleInsurance,
  updateVehicleInsurance,
  deactivateVehicleInsurance,
} from "../../../../api/vehicles/insurance";
import { DocumentUploadSectionV5 } from "../../Media/DocumentUploadSectionV5";
import { DocumentCenter } from "../../documents/document-center";

export const VehicleInsurancePanel = ({ vehicleId }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [openDocsForId, setOpenDocsForId] = useState(null);
  const [docRefreshCounters, setDocRefreshCounters] = useState({});

  const activeInsurance = useMemo(() => items.find((i) => i.active), [items]);

  useEffect(() => {
    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetchVehicleInsurances({
          filters: [{ name: "vehicle_id", value: vehicleId }],
          per_page: 50,
          sortBy: "start_date",
          sort: "DESC",
        });
        setItems(res?.data || []);
      } catch (e) {
        setError("Failed to load insurances");
      } finally {
        setIsLoading(false);
      }
    }
    if (vehicleId) load();
  }, [vehicleId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const form = e.target;
    const payload = {
      vehicle_id: vehicleId,
      insurance_company: form.insurance_company.value,
      policy_number: form.policy_number.value,
      start_date: form.start_date.value,
      premium_nok: form.premium_nok.value || null,
      notes: form.notes.value,
      active: true,
    };
    const res = await createVehicleInsurance(payload);
    if (res?.id) {
      setItems([res, ...items]);
      setShowCreate(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const form = e.target;
    const payload = {
      id: editItem.id,
      vehicle_id: vehicleId,
      insurance_company: form.insurance_company.value,
      policy_number: form.policy_number.value,
      start_date: form.start_date.value,
      end_date: form.end_date.value || null,
      premium_nok: form.premium_nok.value || null,
      notes: form.notes.value,
      active: form.active.checked,
    };
    const res = await updateVehicleInsurance(payload);
    if (res?.id) {
      setItems(items.map((x) => (x.id === res.id ? res : x)));
      setEditItem(null);
    }
  };

  const handleDeactivate = async (item) => {
    const end = item.end_date || new Date().toISOString().slice(0, 10);
    const res = await deactivateVehicleInsurance(item.id, end);
    if (res?.id) {
      setItems(items.map((x) => (x.id === res.id ? res : x)));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t("Insurance")}</h3>
          <p className="text-sm text-gray-500">{t("Manage insurances for this vehicle")}</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
        >
          {t("Add Insurance")}
        </button>
      </div>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {isLoading ? (
            <div className="text-sm text-gray-500">{t("Loading...")}</div>
          ) : error ? (
            <div className="text-sm text-red-600">{error}</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-gray-500">{t("No insurances yet")}</div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {items.map((ins) => (
                <li key={ins.id} className="py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">
                        {ins.insurance_company} — {ins.policy_number}
                      </div>
                      <div className="text-sm text-gray-600">
                        {t("From")}: {ins.start_date}
                        {ins.end_date ? ` • ${t("To")}: ${ins.end_date}` : ""}
                      </div>
                      {ins.premium_nok && (
                        <div className="text-sm text-gray-600">{t("Premium")}: {ins.premium_nok} NOK</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {ins.active && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">
                          {t("Active")}
                        </span>
                      )}
                      <button
                        onClick={() => setOpenDocsForId(openDocsForId === ins.id ? null : ins.id)}
                        className="text-sm text-gray-700 hover:text-gray-900"
                      >
                        {t("Documents")}
                      </button>
                      <button
                        onClick={() => setEditItem(ins)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        {t("Edit")}
                      </button>
                      {ins.active && (
                        <button
                          onClick={() => handleDeactivate(ins)}
                          className="text-sm text-gray-700 hover:text-gray-900"
                        >
                          {t("Deactivate")}
                        </button>
                      )}
                    </div>
                  </div>
                  {openDocsForId === ins.id && (
                    <div className="mt-3 border-t pt-3">
                      <div className="mb-3">
                        <DocumentUploadSectionV5
                          module="vehicle_insurance"
                          moduleParam={ins.id}
                          parentCallback={() => {}}
                          onDocumentUploaded={() =>
                            setDocRefreshCounters((prev) => ({
                              ...prev,
                              [ins.id]: (prev[ins.id] || 0) + 1,
                            }))
                          }
                          multiple={true}
                          iconOnly={false}
                        />
                      </div>
                      <DocumentCenter
                        key={`ins-docs-${ins.id}-${docRefreshCounters[ins.id] || 0}`}
                        module="vehicle_insurance"
                        moduleParam={ins.id}
                        title={t("Documents")}
                        subtitle=""
                        canUpload={false}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <form onSubmit={handleCreate} className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="text-lg font-semibold mb-4">{t("Add Insurance")}</div>
            <div className="space-y-3">
              <input name="insurance_company" className="w-full border rounded px-3 py-2" placeholder={t("Insurance Company")} required />
              <input name="policy_number" className="w-full border rounded px-3 py-2" placeholder={t("Policy Number")} required />
              <input name="start_date" type="date" className="w-full border rounded px-3 py-2" required />
              <input name="premium_nok" type="number" step="0.01" className="w-full border rounded px-3 py-2" placeholder={t("Premium (NOK)")} />
              <textarea name="notes" className="w-full border rounded px-3 py-2" placeholder={t("Notes (optional)")} />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowCreate(false)} className="px-3 py-2 rounded border">{t("Cancel")}</button>
              <button type="submit" className="px-3 py-2 rounded bg-blue-600 text-white">{t("Save")}</button>
            </div>
          </form>
        </div>
      )}

      {editItem && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <form onSubmit={handleUpdate} className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="text-lg font-semibold mb-4">{t("Edit Insurance")}</div>
            <div className="space-y-3">
              <input name="insurance_company" defaultValue={editItem.insurance_company} className="w-full border rounded px-3 py-2" placeholder={t("Insurance Company")}
                required />
              <input name="policy_number" defaultValue={editItem.policy_number} className="w-full border rounded px-3 py-2" placeholder={t("Policy Number")} required />
              <input name="start_date" type="date" defaultValue={editItem.start_date} className="w-full border rounded px-3 py-2" required />
              <input name="end_date" type="date" defaultValue={editItem.end_date || ""} className="w-full border rounded px-3 py-2" />
              <input name="premium_nok" type="number" step="0.01" defaultValue={editItem.premium_nok || ""} className="w-full border rounded px-3 py-2" placeholder={t("Premium (NOK)")} />
              <textarea name="notes" defaultValue={editItem.notes || ""} className="w-full border rounded px-3 py-2" placeholder={t("Notes")}></textarea>
              <label className="flex items-center gap-2 text-sm">
                <input name="active" type="checkbox" defaultChecked={editItem.active} /> {t("Active")}
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setEditItem(null)} className="px-3 py-2 rounded border">{t("Cancel")}</button>
              <button type="submit" className="px-3 py-2 rounded bg-blue-600 text-white">{t("Save")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default VehicleInsurancePanel;


